import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../icons/Icon';
import { NAV_DESTINATIONS, type NavDestination } from '../../config/navigation';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (sectionKey: string, tabKey: string) => void;
}

/**
 * Thirty destinations sit behind the sidebar. Once a console is that deep, the
 * fastest route to a known view is typing its name, not hunting a tree — so
 * Cmd/Ctrl-K opens this and every sidebar item is reachable in two keystrokes.
 */
const SUBSTRING_RANK = 50;

function score(destination: NavDestination, query: string): number {
  const haystack = `${destination.sectionLabel} ${destination.tabLabel} ${destination.description}`.toLowerCase();
  const needle = query.toLowerCase().trim();
  if (!needle) return 1;
  if (destination.tabLabel.toLowerCase().startsWith(needle)) return 100;
  if (destination.sectionLabel.toLowerCase().startsWith(needle)) return 80;
  if (haystack.includes(needle)) return SUBSTRING_RANK;

  // Subsequence match, so "salean" still finds Sales → Sales Analytics.
  let cursor = 0;
  for (const character of needle) {
    cursor = haystack.indexOf(character, cursor);
    if (cursor === -1) return 0;
    cursor += 1;
  }
  return 10;
}

const CommandPalette: React.FC<CommandPaletteProps> = ({ isOpen, onClose, onNavigate }) => {
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const results = useMemo(() => {
    const ranked = NAV_DESTINATIONS.map((destination) => ({ destination, rank: score(destination, query) })).filter(
      (entry) => entry.rank > 0
    );

    // Subsequence hits are a fallback, not a peer. Typing "eslint" matches
    // Static Code Testing on its description and "Sales & Transactions /
    // Orders" only as scattered letters — listing both together buries the
    // answer, so the loose matches are dropped whenever a substring match
    // exists.
    const best = ranked.reduce((highest, entry) => Math.max(highest, entry.rank), 0);
    const floor = best >= SUBSTRING_RANK ? SUBSTRING_RANK : 0;

    return ranked
      .filter((entry) => entry.rank >= floor)
      .sort((a, b) => b.rank - a.rank)
      .slice(0, 8)
      .map((entry) => entry.destination);
  }, [query]);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setHighlighted(0);
      // Focus after paint so the dialog is in the DOM.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isOpen]);

  useEffect(() => {
    setHighlighted(0);
  }, [query]);

  useEffect(() => {
    listRef.current?.querySelector('[data-highlighted="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [highlighted]);

  if (!isOpen) return null;

  const commit = (destination: NavDestination | undefined) => {
    if (!destination) return;
    onNavigate(destination.sectionKey, destination.tabKey);
    onClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlighted((current) => (results.length ? (current + 1) % results.length : 0));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted((current) => (results.length ? (current - 1 + results.length) % results.length : 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      commit(results[highlighted]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[12vh]">
      <button
        type="button"
        aria-label="Close search"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-deep-navy/40 backdrop-blur-[2px]"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search the console"
        className="relative w-full max-w-xl overflow-hidden rounded-card border border-border-default bg-surface shadow-popover"
      >
        <div className="flex items-center gap-3 border-b border-border-subtle px-4">
          <span className="text-ink-3">
            <Icon name="search" className="h-[18px] w-[18px]" />
          </span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Jump to a view…"
            aria-label="Search the console"
            aria-controls="command-palette-results"
            aria-activedescendant={results[highlighted] ? `command-result-${highlighted}` : undefined}
            className="w-full bg-transparent py-3.5 text-sm text-ink placeholder:text-ink-3 focus:outline-none"
          />
          <kbd className="hidden rounded border border-border-default px-1.5 py-0.5 text-[10px] font-semibold text-ink-3 sm:block">
            ESC
          </kbd>
        </div>

        {results.length ? (
          <ul id="command-palette-results" ref={listRef} role="listbox" aria-label="Results" className="max-h-80 overflow-y-auto p-2">
            {results.map((destination, index) => (
              <li key={`${destination.sectionKey}-${destination.tabKey}`}>
                <button
                  type="button"
                  id={`command-result-${index}`}
                  role="option"
                  aria-selected={index === highlighted}
                  data-highlighted={index === highlighted}
                  onMouseEnter={() => setHighlighted(index)}
                  onClick={() => commit(destination)}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
                    index === highlighted ? 'bg-surface-active' : 'hover:bg-surface-hover'
                  }`}
                >
                  <span className="text-brand-blue">
                    <Icon name={destination.icon} className="h-[18px] w-[18px]" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-ink">
                      {destination.sectionLabel}
                      <span className="mx-1.5 text-ink-3">/</span>
                      {destination.tabLabel}
                    </span>
                    <span className="block truncate text-xs text-ink-3">{destination.description}</span>
                  </span>
                  <span className="shrink-0 rounded bg-surface-sunken px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ink-3">
                    {destination.group}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-4 py-8 text-center text-sm text-ink-3">
            No view matches “{query}”.
          </p>
        )}
      </div>
    </div>
  );
};

export default CommandPalette;
